import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface ProductCheckup {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  checkup_name: string;
  checkup_description: string;
  checkup_code: string;
  checkup_type: string;
  checkup_price: string;
  tests: any[];
}

interface CheckupsState {
  checkups: ProductCheckup[];
  selectedCheckup: ProductCheckup | null;
  loading: boolean;
  error: string | null;
}

const initialState: CheckupsState = {
  checkups: [],
  selectedCheckup: null,
  loading: false,
  error: null,
};

export const fetchCheckups = createAsyncThunk(
  'checkups/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.pms}/api/v1/checkups`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch checkups');
    }
  }
);

export const fetchCheckupById = createAsyncThunk(
  'checkups/fetchById',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.pms}/api/v1/checkups/${id}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch checkup');
    }
  }
);

export const createCheckup = createAsyncThunk(
  'checkups/create',
  async (data: Partial<ProductCheckup>, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.pms}/api/v1/checkups`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to create checkup');
    }
  }
);

export const updateCheckup = createAsyncThunk(
  'checkups/update',
  async ({ id, data }: { id: number; data: Partial<ProductCheckup> }, thunkAPI) => {
    try {
      const response = await apiClient.put(`${config.services.pms}/api/v1/checkups/${id}`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to update checkup');
    }
  }
);

export const deleteCheckup = createAsyncThunk(
  'checkups/delete',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.pms}/api/v1/checkups/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete checkup');
    }
  }
);

const checkupsSlice = createSlice({
  name: 'checkups',
  initialState,
  reducers: {
    clearSelectedCheckup: (state) => { state.selectedCheckup = null; },
    clearCheckupsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCheckups.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchCheckups.fulfilled, (state, action) => { state.loading = false; state.checkups = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchCheckups.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createCheckup.fulfilled, (state, action) => {
        const c = action.payload?.data || action.payload;
        if (c) state.checkups.push(c);
      })
      .addCase(updateCheckup.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.checkups.findIndex(c => c.ID === updated.ID);
          if (idx !== -1) state.checkups[idx] = updated;
        }
      })
      .addCase(deleteCheckup.fulfilled, (state, action) => {
        state.checkups = state.checkups.filter(c => c.ID !== action.payload);
      });
  },
});

export const { clearSelectedCheckup, clearCheckupsError } = checkupsSlice.actions;
export default checkupsSlice.reducer;
